import type { QueryClient } from "@tanstack/react-query";

import { processedInvoiceQueries } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import { orderQueries } from "~/modules/project/api/order/orderQueries.ts";
import { PROJECT_QUERY_KEY } from "~/modules/project/api/project/projectQueries.ts";
import { PROJECTS_PHASES_QUERY_KEY } from "~/modules/project/api/projectPhase/projectPhaseQueries.ts";
import { quotaQueries } from "~/modules/quota/api/quota/quotaQueries.ts";
import type { QuotaConsumer, QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";

// The projects of the consumer orders. It reads the order embed of each consumer (BE1).
export function getQuotaConsumerProjectIds(consumers: QuotaConsumer[]): string[] {
	const projectIds = consumers
		.map((consumer) => consumer.order?.projectId)
		.filter((projectId): projectId is string => !!projectId);
	return [...new Set(projectIds)];
}

// Invalidates all quota queries: details, index, indexByCompany, available consumers and consumers.
// A quota select in the time tracking form must use a key below quotaQueries.baseKey to reload here too.
// The returned promise resolves after the active queries are refetched.
export function invalidateQuotaQueries(queryClient: QueryClient) {
	return queryClient.invalidateQueries({ queryKey: quotaQueries.baseKey });
}

// The orders, phases and project of a project carry the QuotaReference and the quota budget of their
// orders. A consumer change, a title change or a delete changes them.
export function invalidateProjectQuotaQueries(queryClient: QueryClient, projectIds: string[]) {
	projectIds.forEach((projectId) => {
		void queryClient.invalidateQueries({ queryKey: orderQueries.byProject(projectId).queryKey });
		void queryClient.invalidateQueries({ queryKey: PROJECTS_PHASES_QUERY_KEY(projectId) });
		void queryClient.invalidateQueries({ queryKey: PROJECT_QUERY_KEY(projectId) });
	});
}

// An attach or a detach changes the quota amount, the quota link of the invoices and the eligible
// invoices. The amount also shows in the projects of the consumers.
export async function invalidateQuotaInvoiceChangeQueries(queryClient: QueryClient, quota: QuotaWithRelations) {
	await invalidateQuotaQueries(queryClient);
	void queryClient.invalidateQueries({ queryKey: processedInvoiceQueries.baseKey });
	invalidateProjectQuotaQueries(queryClient, getQuotaConsumerProjectIds(quota.consumers));
}
